import {
  UpdateVehicleInput,
  Currency,
  HireUnitType,
  VehicleType,
} from "@/gql/graphql";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { RichTextInput } from "@/lib/Components/Form/Inputs/RichTextInput";
import { VehicleTypeInput } from "@/app/Vehicles/Components/VehicleTypeInput";
import { FuelInput } from "@/app/Vehicles/Components/FuelInput";
import { TransmissionInput } from "@/app/Vehicles/Components/TransmissionInput";
import { VehicleAgeInput } from "@/app/Vehicles/Components/VehicleAgeInput";
import { NumberInput } from "@/lib/Components/Form/Inputs/NumberInput";
import { HireUnitTypeInput } from "@/app/Common/Components/HireUnitType";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { TrueFalseNullInput } from "@/lib/Components/Form/Inputs/TrueFalseNullInput";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useTranslations } from "use-intl";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { supplierRecordQuery } from "@/app/Suppliers/GraphQL/supplierRecordQuery";

export function VehicleBasicForm() {
  const t = useTranslations("vehicle.form");

  return (
    <>
      <TextInput name="name" label={t("name")} tooltip={t("name_tooltip")} />
      <TextInput
        name="code"
        label={t("code")}
        optionalLabel
        tooltip={t("code_tooltip")}
      />
      <div className="col-span-full">
        <RichTextInput
          optionalLabel
          name="description"
          label={t("description")}
          className="col-span-full"
        />
      </div>
      <TextInput name="brand" label={t("brand")} optionalLabel />
      <TextInput name="model" label={t("model")} optionalLabel />
    </>
  );
}

export function VehicleSpecificationsForm() {
  const t = useTranslations("vehicle.form");
  const { values } = useFormState<UpdateVehicleInput>();
  const isCaravan = values.type === VehicleType.Caravan;

  return (
    <>
      <VehicleTypeInput name="type" label={t("vehicle_type")} />
      {isCaravan ? null : (
        <>
          <FuelInput name="fuel" label={t("fuel")} />
          <TransmissionInput name="transmission" label={t("transmission")} />
          <VehicleAgeInput
            optionalLabel
            name="approximate_age"
            label={t("vehicle_age")}
          />
          <NumberInput
            name="seatbelts"
            label={t("seatbelts")}
            inputProps={{ min: 0 }}
          />
          <NumberInput
            name="child_seat_anchor_points"
            label={t("child_seat_anchors")}
            optionalLabel
            inputProps={{ min: 0 }}
          />
        </>
      )}
    </>
  );
}

export function VehicleRatesForm() {
  const { values, setFieldValue } = useFormState<UpdateVehicleInput>();
  const { isAdmin } = useTenant();
  const t = useTranslations("vehicle.form");

  const { data } = useGqlQuery(
    supplierRecordQuery,
    { id: values.supplier?.connect! },
    { enabled: !!values.supplier?.connect },
  );

  const currency = data?.supplier.currency ?? Currency.Usd;

  return (
    <>
      <HireUnitTypeInput name="hire_unit_type" label={t("charged_per")} />
      <CurrencyInput
        isEditable={false}
        name="extra_hire_unit_rate"
        currency={currency}
        label={t("extra_hire_unit_rate", {
          unit: t(
            `../hire_unit_type.${values.hire_unit_type ?? HireUnitType.Day}`,
          ).toLowerCase(),
        })}
      />
      <CurrencyInput
        currency={currency}
        onChange={(newValue) => {
          if (newValue === null) return;
          setFieldValue(
            "extra_hire_unit_rate",
            newValue + (values?.extra_hire_unit_deposit_rate ?? 0),
          );
        }}
        name="extra_hire_unit_supplier_net_rate"
        label={t("supplier_net")}
      />
      <CurrencyInput
        currency={currency}
        isEditable={isAdmin}
        name="extra_hire_unit_deposit_rate"
        onChange={(value) => {
          setFieldValue(
            "extra_hire_unit_rate",
            (value ?? 0) + (values?.extra_hire_unit_supplier_net_rate ?? 0),
          );
        }}
        label={t("imoova_net")}
      />
      {isAdmin && (
        <CurrencyInput
          name="hire_unit_rate"
          label={t("relocation_rate")}
          currency={currency}
        />
      )}
    </>
  );
}

export function VehicleFeaturesForm() {
  const { values } = useFormState<UpdateVehicleInput>();
  const t = useTranslations("vehicle.form");
  const { data } = useGqlQuery(
    supplierRecordQuery,
    { id: values.supplier?.connect! },
    { enabled: !!values.supplier?.connect },
  );

  const currency = data?.supplier.currency ?? Currency.Usd;
  const isSleeper = values.type
    ? [
        VehicleType.FourWheelDriveCamper,
        VehicleType.CamperVan,
        VehicleType.MotorHome,
        VehicleType.Caravan,
      ].includes(values.type)
    : false;

  return (
    <>
      <TrueFalseNullInput
        name="is_pet_friendly"
        label={t("pet_friendly")}
        optionalLabel
      />

      {values.is_pet_friendly && (
        <CurrencyInput
          name="pet_cleaning_fee"
          label={t("pet_cleaning_fee")}
          currency={currency}
          optionalLabel
        />
      )}

      {isSleeper && (
        <>
          <NumberInput
            name="sleeps"
            label={t("sleeps")}
            inputProps={{ min: 0 }}
          />
          <div className="col-span-full" />
          <TrueFalseNullInput
            label={t("self_contained")}
            name="is_self_contained"
          />
          <TrueFalseNullInput label={t("kitchen")} name="has_kitchen" />
          <TrueFalseNullInput
            label={t("shower_and_toilet")}
            name="has_shower_and_toilet"
          />
          <TrueFalseNullInput label={t("heating")} name="has_heating" />
        </>
      )}
    </>
  );
}
